// @flow
import ModularUIResponse from "../../modularui/ModularUIResponse";

import ResourceModel from "../base/ResourceModel";
import FilterCollection from "../filters/FilterCollection";
import ContentLinkModel from "./ContentLinkModel";
import SubSectionModel from "./SubSectionModel";

import ContentTypeModel from "./ContentTypeModel";

import { TIMEVERSION_FILTER_NAME } from "../../constants/Constants";

import type { ModularUIModel } from "../types";
import type LinkModel from "../links/LinkModel";
import type Href from "../href/Href";
import SectionModel from "./SectionModel";

/**
 * Content detail model
 */
export default class ContentModel extends ResourceModel {
  _section: SectionModel;
  _contentType: ContentTypeModel | null = null;
  _filterCollection: FilterCollection;

  constructor(response: ModularUIResponse) {
    super(response);

    this._section = new SectionModel(
      this.data,
      this.entryDate,
      this.modelOptions,
    );
  }

  /**
   */
  get type(): string {
    return "Content";
  }

  /**
   */
  static get modelName(): string {
    return "ContentModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    return (
      data.contributions.resourcetype &&
      data.contributions.resourcetype === "ContentDetail"
    );
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    const contentTypeLink = this.links.getLinkByKey("contenttype");

    if (contentTypeLink) {
      return [contentTypeLink];
    }

    return [];
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>) {
    this.contentType = models.find((model) => model.type === "ContentType");
  }

  /**
   */
  get section(): SectionModel {
    return this._section;
  }

  /**
   */
  get id(): string {
    return this.data._id;
  }

  /**
   * Retrieve available filters on concept toc
   */
  get filterCollection(): FilterCollection {
    if (!this._filterCollection && this.data.filter?.entryDate) {
      this._filterCollection = new FilterCollection(
        {
          entryDate: this.data.filter.entryDate,
        },
        {
          filter: this.contributions.filter,
        },
        this.modelOptions,
      );
    }

    return this._filterCollection;
  }

  /**
   * Retrieve modelcatalog.js of content toc
   */
  get entryDate(): ISO_DATE | null {
    const entryDateFilter = this.filterCollection?.getFilterByAttributeKey(
      TIMEVERSION_FILTER_NAME,
    );

    const date = entryDateFilter?.value;
    if (typeof date === "string") {
      return date;
    }
    return null;
  }

  /**
   * Get conceptType of concept
   */
  get contentType(): ContentTypeModel | null {
    return this._contentType;
  }

  /**
   * Set concept type
   */
  set contentType(contentType: ?ModularUIModel) {
    this._contentType =
      contentType instanceof ContentTypeModel ? contentType : null;
  }

  /**
   * Get content label
   */
  get label(): string {
    return this.section.label;
  }

  /**
   * Retrieve number of section
   */
  get number(): string | null {
    return this.section.number;
  }

  /**
   * Get content body
   */
  get body(): ?string {
    return this.section.body;
  }

  /**
   * Retrieve child section links
   */
  get childSectionLinks(): Array<ContentLinkModel> {
    return this.section.childSectionLinks;
  }

  /**
   */
  get selfContentLink(): ContentLinkModel {
    return this.section.selfContentLink;
  }

  /**
   * Get tree of child sections
   */
  get childSections(): Array<SectionModel> {
    return this.section.childSections;
  }

  /**
   * set resolved child sections
   */
  set childSections(sections: Array<SectionModel>) {
    this.section.childSections = sections || [];
  }

  /**
   * Get sub sections
   */
  get subSections(): Array<SubSectionModel> {
    return this.section.subSections;
  }

  /**
   */
  get relatedConceptsHrefs(): Array<Href> {
    const hrefs = [];
    if (this.relatedConceptsHref) {
      hrefs.push(this.relatedConceptsHref);
    }

    this.subSections.forEach((subSection) => {
      hrefs.push(...subSection.relatedConceptsHrefs);
    });

    return hrefs;
  }

  /**
   * Get related concepts link
   */
  get relatedConceptsHref(): Href | null {
    const relatedConceptsLink = this.links.getLinkByKey("relatedConcepts");

    if (relatedConceptsLink) {
      const { href } = relatedConceptsLink;
      href.addParameter("entryDate", this.entryDate);
      href.setReferenceHash(this.referenceHash);
      return href;
    }

    return null;
  }

  /**
   */
  get referenceHash(): number {
    return this.section.referenceHash;
  }
}
